import { Capacitor } from '@capacitor/core';
import { CAPACITOR_SAFE_AREA_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import {
    SafeArea,
    SafeAreaBottomKeyboardData,
    SafeAreaData,
    SafeAreaInsetData,
} from '../../../capacitor_plugins/safeArea';
import safeAreaSingleton from './safeAreaSingleton';

/**
 * A window event rather than a `safeAreaSingleton.subscribe()`: the singleton and its callers land in
 * different webpack chunks, so a stale service worker cache serving one of each would throw
 * `subscribe is not a function`. An event fails soft — nobody dispatching it just means no updates.
 */
export const SAFE_AREA_CHANGED_EVENT = 'milanoteSafeAreaChanged';

const updateSafeAreaValues = (safeAreaData: SafeAreaData) => {
    const body = document.body;

    safeAreaSingleton.setSafeAreaData(safeAreaData);

    (Object.keys(safeAreaData.safeAreaInsets) as Array<keyof SafeAreaInsetData>)?.forEach((key) => {
        body.style.setProperty(`--capacitor-safe-area-${key}`, `${safeAreaData.safeAreaInsets[key] || 0}px`);
    });

    (Object.keys(safeAreaData.safeAreaKeyboard) as Array<keyof SafeAreaBottomKeyboardData>)?.forEach((key) => {
        body.style.setProperty(`--keyboard-toolbar-bottom-${key}`, `${safeAreaData.safeAreaKeyboard[key] || 0}px`);
    });

    window.dispatchEvent(new Event(SAFE_AREA_CHANGED_EVENT));
};

/**
 * Adds the safe area values to the root element.
 * This is necessary especially for loading screens since the css safe-area-inset-* values
 * are not available immediately and cause a flicker when the safe area is applied.
 */
export const addCapacitorSafeAreaValues = () => {
    if (Capacitor.isPluginAvailable(CAPACITOR_SAFE_AREA_PLUGIN)) {
        (async () => {
            await SafeArea.currentSafeArea().then(updateSafeAreaValues);

            SafeArea.addListener('safeAreaChanged', updateSafeAreaValues);
        })();
    }
};
